import { getMachinesSearchTaggingQueryKey } from "@aura-frontend/omnibus-az-client";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "@win-reality/react-ui";
import { useCallback } from "react";

import { SCHEDULES_QUERY_KEY } from "@/pages/Complexes/SingleField/FieldSchedule/FieldSchedule.queries";
import { handleErrorNotification } from "@/pages/Tagging/utils/handleErrorNotification";

import { editGameEndTime } from "../api/gameEndTime";
import { GameHistoryStatus } from "../api/gameHistory";
import { GameStreamEvent } from "../api/gameStream";
import { toLineupPosition } from "../api/omnibusMappers";
import { setRosterJerseyNumber } from "../api/rosterJersey";
import { TEAM_ROSTER_QUERY_KEY } from "../api/teamRoster";
import { setHomeTeam } from "../api/teamSwap";
import { useGameWrites } from "../api/useGameWrites";
import { TeamSide } from "../constants/teams";
import { GamePlayerRef, LineUpPlayer } from "../types/gameFull";
import { TagPlayerOverrides } from "../utils/buildTagPayload";
import { diffLineupDraft, hasLineupChanges } from "../utils/lineupDiff";
import {
  applyLineupDraft,
  getBattingOrder,
  LineupDraft,
} from "../utils/lineupDraft";
import { GameStateEdit } from "../utils/pitchTagging";
import {
  applyPlateAppearanceEdit,
  PlateAppearanceEditValues,
} from "../utils/plateAppearanceEditor";
import { getLastTaggedPitch } from "../utils/plateAppearanceFeed";
import { findLineupPlayer, toPlayerRef } from "../utils/playerChange";
import { setPlayerJerseyNumber } from "../utils/playerEdit";
import { toPitchStatuses } from "../utils/tagQueue";
import { useActiveGame } from "./useActiveGame";
import { useGameRosters } from "./useGameRosters";
import { useScorekeepingGame } from "./useScorekeepingGame";
import { useTagQueue } from "./useTagQueue";
import { useTagSaves } from "./useTagSaves";

type ScorekeepingGame = ReturnType<typeof useScorekeepingGame>;

const SIDES: TeamSide[] = ["away", "home"];

/**
 * Lineup management + at-bat editing actions for Scorekeeping V2: each saves
 * through omnibus, then applies the change to the local game state.
 * Errors are shown as toasts; the actions resolve to null/false on failure.
 */
export const useScorekeepingActions = (
  game: ScorekeepingGame,
  /** Background re-tags and fills wait for the game's older pitches */
  historyStatus: GameHistoryStatus,
) => {
  const { gameId, machineUuid, teamIds, times } = useActiveGame();
  const { rosters, isLoading: isLoadingRosters } = useGameRosters({
    teamIds,
    teamNames: game.data.game.teams,
    setRosters: game.setRosters,
  });
  const writes = useGameWrites({ gameId, machineUuid, rosters });
  const queryClient = useQueryClient();

  const canWrite = writes.canWrite;

  const tagSaves = useTagSaves({ game, writes });
  const tagQueue = useTagQueue({
    data: game.data,
    writes,
    savingIds: tagSaves.pendingTagIds,
    historyStatus,
  });
  const pitchStatuses = toPitchStatuses(
    tagSaves.pendingTagIds,
    tagQueue.queued.queue,
    tagQueue.queued.inFlight,
  );

  const { receiveStreamEvent: receiveSaveEvent } = tagSaves;
  const { receiveStreamEvent: receiveQueueEvent } = tagQueue;
  // Stable: the game stream's handler depends on it
  const receiveTagStreamEvent = useCallback(
    (event: GameStreamEvent) => {
      receiveSaveEvent(event);
      receiveQueueEvent(event);
    },
    [receiveSaveEvent, receiveQueueEvent],
  );

  /**
   * Saves through omnibus (when there's a game to write to), then applies the
   * change locally. Resolves to false, with an error toast, if either fails.
   */
  const writeThenApply = async (
    errorMessage: string,
    write: () => Promise<unknown>,
    apply: () => void,
  ) => {
    try {
      if (canWrite) await write();
      apply();
      return true;
    } catch (error) {
      handleErrorNotification(errorMessage, error as Error);
      return false;
    }
  };

  const findLineupEntry = (side: TeamSide, playerId: string) =>
    game.data.lineUp[side].find((p) => p.playerId === playerId);

  /**
   * New hitter/pitcher for a PA: a player tag on the active at-bat (none of
   * its pitches need be tagged), then its pitches are re-tagged with them in
   * the background, so every omnibus tag of the PA names them.
   */
  const writePlateAppearancePlayers = async (
    plateAppearanceId: string,
    overrides: TagPlayerOverrides,
  ) => {
    if (plateAppearanceId === game.data.game.currentPlateAppearanceId) {
      await writes.tagActivePlayers(game.data, overrides);
    }
    tagQueue.enqueuePlayers(plateAppearanceId, overrides);
  };

  /**
   * Lineup panel close: new players join their team's roster first. A team
   * whose players were only added gets them at the end of its batting order,
   * one substitution with no outgoing player each (omnibus and the game
   * service both append those). Reorders and removals need both teams'
   * lineups saved whole. Resolves to false (and keeps the lineups as they
   * were) if anything fails, so the panel stays open with the edits.
   */
  const saveLineups = async (draft: LineupDraft) => {
    const diff = (side: TeamSide) =>
      diffLineupDraft(game.data.lineUp[side], draft[side].battingOrder);
    const diffs = { away: diff("away"), home: diff("home") };
    const changedSides = SIDES.filter((side) => hasLineupChanges(diffs[side]));
    if (!canWrite || changedSides.length === 0) {
      game.applyLineups(draft);
      return true;
    }

    try {
      let saved = draft;
      for (const side of changedSides) {
        const { additions } = diffs[side];
        const added = await writes.ensureOnRoster(side, additions);
        // New players swap their local ids for omnibus ones
        const ids = new Map(additions.map((p, i) => [p.playerId, added[i]]));
        const withIds = (players: LineUpPlayer[]) =>
          players.map((p) => ids.get(p.playerId) ?? p);
        saved = {
          ...saved,
          [side]: {
            ...saved[side],
            battingOrder: withIds(saved[side].battingOrder),
            roster: withIds(saved[side].roster),
          },
        };
      }

      const appendSides = changedSides.filter(
        (side) => !diffs[side].needsReorder,
      );
      for (const side of appendSides) {
        const slots = getBattingOrder(game.data.lineUp[side]).length;
        const appended = saved[side].battingOrder.slice(slots);
        // One at a time, so each lands in the next slot
        for (const [index, player] of appended.entries()) {
          await writes.substitute({
            side,
            inPlayer: player,
            position: slots + index + 1,
          });
        }
      }

      if (changedSides.some((side) => diffs[side].needsReorder)) {
        await writes.saveLineups(
          applyLineupDraft(game.data, game.rosters, saved).data.lineUp,
        );
      }
      game.applyLineups(saved);
      return true;
    } catch (error) {
      handleErrorNotification("Couldn't save lineup changes", error as Error);
      return false;
    }
  };

  /** Adds players to a team's roster; resolves to them with real ids. */
  const addRosterPlayers = async (side: TeamSide, players: LineUpPlayer[]) => {
    try {
      const added = canWrite
        ? await writes.ensureOnRoster(side, players)
        : players;
      game.addRosterPlayers(side, added);
      return added;
    } catch (error) {
      handleErrorNotification("Couldn't add players to roster", error as Error);
      return null;
    }
  };

  /**
   * Change Hitter: saves the PA's new hitter. On the active at-bat they also
   * become the live hitter until the game service's state arrives.
   */
  const changeHitter = ({
    plateAppearanceId,
    player,
  }: {
    plateAppearanceId: string;
    player: LineUpPlayer;
  }) =>
    writeThenApply(
      "Couldn't change hitter",
      () => writePlateAppearancePlayers(plateAppearanceId, { hitter: player }),
      () => {
        game.setPlateAppearancePlayers(plateAppearanceId, {
          hitter: toPlayerRef(player),
        });
        if (plateAppearanceId === game.data.game.currentPlateAppearanceId) {
          game.setActiveHitter(player);
        }
      },
    );

  /**
   * Sub Out: the incoming player joins the roster if needed, replaces the
   * outgoing one in the lineup, and becomes the PA's hitter/pitcher.
   * Resolves to the incoming player (with their omnibus id).
   */
  const subOut = async ({
    side,
    role,
    plateAppearanceId,
    outPlayer,
    inPlayer,
  }: {
    side: TeamSide;
    role: "hitter" | "pitcher";
    plateAppearanceId: string;
    outPlayer: GamePlayerRef;
    inPlayer: LineUpPlayer;
  }) => {
    const isHitter = role === "hitter";
    const outEntry = findLineupEntry(side, outPlayer.playerId);
    // A hitter missing from the lineup (the unknown hitter of a game started
    // without one) has no slot to hand over, so the sub takes the next one
    const nextSlot = getBattingOrder(game.data.lineUp[side]).length + 1;

    try {
      let sub = inPlayer;
      if (canWrite) {
        [sub] = await writes.ensureOnRoster(side, [inPlayer]);
        const position = !isHitter
          ? "pitcher"
          : outEntry
            ? toLineupPosition(outEntry)
            : nextSlot;
        if (position == null) {
          throw new Error(`${outPlayer.playerName} isn't in the batting order`);
        }
        // A pitcher or hitter missing from the lineup comes in without
        // replacing anyone
        await writes.substitute({
          side,
          inPlayer: sub,
          outPlayer: outEntry,
          position,
        });
        await writePlateAppearancePlayers(
          plateAppearanceId,
          isHitter
            ? {
                hitter: {
                  ...sub,
                  battingOrder: outEntry ? outEntry.battingOrder : nextSlot,
                },
              }
            : { pitcher: sub },
        );
      }

      game.substitutePlayer(
        side,
        outPlayer.playerId,
        sub,
        isHitter ? { battingOrder: nextSlot } : { position: "P" },
      );
      game.setPlateAppearancePlayers(
        plateAppearanceId,
        isHitter ? { hitter: toPlayerRef(sub) } : { pitcher: toPlayerRef(sub) },
      );
      return sub;
    } catch (error) {
      handleErrorNotification(`Couldn't sub out ${role}`, error as Error);
      return null;
    }
  };

  /** A PA's player with their lineup details from `side`, if in a lineup */
  const toLineUpPlayer = (
    player: GamePlayerRef,
    side: TeamSide,
  ): LineUpPlayer =>
    findLineupPlayer(game.data, player.playerId, side) ?? {
      playerId: player.playerId,
      name: player.playerName,
    };

  /**
   * Post At-Bat Update: re-tags the PA's last tagged pitch with the edited
   * outs recorded, runs scored and base runners (and its current players),
   * then shows the edit. A new hitter/pitcher reaches the PA's other pitches
   * through the tag queue.
   * The game service applies runs; outs come from the outcome and runners
   * advance by rule there, so those only reach omnibus for now.
   * A new inning is frontend only (the re-tag keeps the PA's inning) until
   * the game service adds an endpoint to move a PA.
   */
  const updatePlateAppearance = (
    plateAppearanceId: string,
    values: PlateAppearanceEditValues,
  ) => {
    const pa = game.data.plateAppearances.find(
      (p) => p.plateAppearanceId === plateAppearanceId,
    );
    const battingSide =
      (pa?.isTopOfInning ?? values.isTopOfInning) ? "away" : "home";
    const hitter = toLineUpPlayer(values.hitter, battingSide);
    const pitcher = toLineUpPlayer(
      values.pitcher,
      battingSide === "away" ? "home" : "away",
    );
    const changedPlayers: TagPlayerOverrides = {
      ...(values.hitter.playerId !== pa?.hitter.playerId && { hitter }),
      ...(values.pitcher.playerId !== pa?.pitcher.playerId && { pitcher }),
    };
    const lastTaggedId = getLastTaggedPitch(
      game.data,
      plateAppearanceId,
    )?.activityId;

    return writeThenApply(
      "Couldn't update the at bat",
      async () => {
        // This re-tag has the newest players and the edit: a queued one for
        // the same pitch is redundant, and one in flight must land first
        if (lastTaggedId) await tagQueue.takeOver(lastTaggedId);
        await writes.retagLastPitch(
          applyPlateAppearanceEdit(game.data, plateAppearanceId, {
            ...values,
            inning: pa?.inning ?? values.inning,
            isTopOfInning: pa?.isTopOfInning ?? values.isTopOfInning,
          }),
          plateAppearanceId,
          { hitter, pitcher },
        );
        if (changedPlayers.hitter || changedPlayers.pitcher) {
          tagQueue.enqueuePlayers(plateAppearanceId, changedPlayers, {
            exclude: lastTaggedId ? [lastTaggedId] : [],
          });
        }
      },
      () => game.editPlateAppearance(plateAppearanceId, values),
    );
  };

  /**
   * Player Details: a player's jersey number. It lives on the team's roster,
   * and the game service keeps it on the lineup row, so both are saved: the
   * roster member (when the player is one), then both lineups restated with
   * the new number (`lineupUpdate`). The game service shows the number only
   * from the lineup, so it's right everywhere, after a reload too. Names
   * aren't edited here: the game service keeps copies of a name that a
   * lineup save doesn't reach (see WRITE_STRATEGY.md).
   */
  const editJerseyNumber = (
    side: TeamSide,
    playerId: string,
    jerseyNumber: string,
  ) =>
    writeThenApply(
      "Couldn't save the jersey number",
      async () => {
        const record = rosters[side]?.record;
        if (record) {
          await setRosterJerseyNumber(record, playerId, jerseyNumber);
        }
        const { lineUp } = game.data;
        await writes.saveLineups({
          away: setPlayerJerseyNumber(lineUp.away, playerId, jerseyNumber),
          home: setPlayerJerseyNumber(lineUp.home, playerId, jerseyNumber),
        });
        await queryClient.invalidateQueries({
          queryKey: [TEAM_ROSTER_QUERY_KEY],
        });
      },
      () => game.setJerseyNumber(playerId, jerseyNumber),
    );

  /**
   * Edit Live Game State: sends a state tag through omnibus and shows the edit
   * until the game service's state arrives, which replaces it either way.
   */
  const editGameState = (edit: GameStateEdit) =>
    writeThenApply(
      "Couldn't update the game state",
      () => writes.tagGameState(game.data, edit),
      () => game.editGameState(edit),
    );

  /**
   * Set Home Team: swaps (or confirms) the game's home and away teams. The
   * swapped game arrives from the game service; the refetch picks up the
   * omnibus game's swapped team ids and colors, which rosters and colors use.
   */
  const changeHomeTeam = (homeSide: TeamSide) =>
    writeThenApply(
      "Couldn't change the home team",
      async () => {
        await setHomeTeam(machineUuid, homeSide);
        await queryClient.refetchQueries({
          queryKey: getMachinesSearchTaggingQueryKey(),
        });
      },
      () => {},
    );

  /**
   * Time Remaining: moves the game's scheduled end. The new end arrives from
   * the game service; the refetches pick up the omnibus game's times (which
   * the next edit sends back) and the field's schedule.
   */
  const changeEndTime = (endTimeS: number) =>
    writeThenApply(
      "Couldn't change the game's end time",
      async () => {
        await editGameEndTime({
          gameId: gameId!,
          times: times ?? [],
          endTimeS,
        });
        await Promise.all([
          queryClient.refetchQueries({
            queryKey: getMachinesSearchTaggingQueryKey(),
          }),
          queryClient.invalidateQueries({ queryKey: [SCHEDULES_QUERY_KEY] }),
        ]);
      },
      () => {},
    );

  /**
   * End Game: fills the open at bat's untagged pitches and waits for the tag
   * queue, then ends the game in omnibus and reloads the SmartPark, which
   * sends the page back to game setup. Resolves to false, with an error
   * toast, if it fails.
   */
  const endGame = async () => {
    try {
      await tagQueue.finish(game.data.game.currentPlateAppearanceId);
      await writes.endGame();
    } catch (error) {
      handleErrorNotification(
        "An error occurred while trying to end the game",
        error as Error,
      );
      return false;
    }
    toast("The game has been ended!", { variant: "success" });
    await queryClient.refetchQueries({
      queryKey: getMachinesSearchTaggingQueryKey(),
    });
    return true;
  };

  return {
    isLoadingRosters,
    saveLineups,
    saveTags: tagSaves.saveTags,
    /** Pitches whose tags are saving or queued; their rows are locked */
    pitchStatuses,
    /** A saved tag hasn't reached the game service yet, so Save must wait */
    isWaitingOnTag: tagSaves.isWaitingOnTag,
    /** Call with every game service WebSocket event */
    receiveTagStreamEvent,
    editGameState,
    changeHomeTeam,
    changeEndTime,
    updatePlateAppearance,
    addRosterPlayers,
    changeHitter,
    subOut,
    editJerseyNumber,
    endGame,
  };
};

export type ScorekeepingActions = ReturnType<typeof useScorekeepingActions>;
